import { useLocation } from "react-router-dom";

/**
 * Extract the current client slug from the URL when the user is inside a
 * `/c/[slug]/*` client-scoped route. Returns `null` when the user is on a
 * global platform route (or on a bare-slug landing page like `/[slug]`).
 *
 * This is the single source of truth for "am I inside a client env right
 * now?" — every `<Link>` / `navigate()` call in the app funnels through it
 * so navigation stays inside `/c/[slug]/*` whenever we are inside it, and
 * stays global when we're not. Never hardcode a slug — always read it here.
 */
export function useSlugScope() {
  const { pathname } = useLocation();
  const m = pathname.match(/^\/c\/([^/]+)(?:\/|$)/);
  return m ? m[1] : null;
}

/**
 * Rewrite a global path into its slug-scoped equivalent when `slug` is
 * present. When `slug` is null (or the caller is outside a client env), the
 * original path is returned unchanged so the same call site works in both
 * the global and slug-scoped contexts without branching.
 *
 *   scoped("/leaderboard", "founders-arms") → "/c/founders-arms/leaderboard"
 *   scoped("/", "founders-arms")            → "/c/founders-arms"
 *   scoped("/leaderboard", null)            → "/leaderboard"
 *
 * Paths with query strings / hash fragments are supported — only the path
 * portion is rewritten:
 *   scoped("/register?client=x", null) → "/register?client=x" (unchanged)
 */
export function scoped(path, slug) {
  if (!slug) return path;
  if (!path || path[0] !== "/") return path;
  if (path === "/") return `/c/${slug}`;
  // Split any query / hash suffix off so we only prefix the path portion.
  const qIdx = path.search(/[?#]/);
  if (qIdx === -1) return `/c/${slug}${path}`;
  return `/c/${slug}${path.slice(0, qIdx)}${path.slice(qIdx)}`;
}

/**
 * Convenience hook: returns a `scope(path)` function pre-bound to the
 * current URL's slug. Prefer this in page components — it lets you write
 * `<Link to={scope("/leaderboard")}>` without threading `slug` through the
 * component tree.
 */
export function useScopedPath() {
  const slug = useSlugScope();
  return (path) => scoped(path, slug);
}
